import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Search, PackagePlus, Truck, AlertTriangle } from "lucide-react";

const CJDropshipping = () => {
  const [status, setStatus] = useState(null);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [prices, setPrices] = useState({});
  const [importing, setImporting] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(null);
  const [bulkFailures, setBulkFailures] = useState([]);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  const testConnection = async () => {
    setHealthLoading(true);
    setHealth(null);
    try {
      const { data } = await api.get("/admin/cj/health");
      setHealth(data);
      if (data.ok) toast.success("CJ is connected and working — you can import now.");
      else if (data.frozen) toast.error("CJ account/API is still frozen.");
      else toast.error("CJ connection problem — see details.");
    } catch (e) {
      setHealth({ ok: false, frozen: false, reason: e.response?.data?.detail || "Connection test failed" });
    } finally {
      setHealthLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/cj/status").then((r) => setStatus(r.data)).catch(() => setStatus({ connected: false }));
  }, []);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try {
      const r = await api.get(`/admin/cj/products`, { params: { q } });
      setResults(r.data.products || []);
      if (!(r.data.products || []).length) toast("No products found for that search.");
    } catch (e) {
      const detail = e.response?.data?.detail || "";
      if (/frozen/i.test(detail)) {
        toast.error("Your CJ account is frozen — unfreeze it at cjdropshipping.com, then search again.");
      } else {
        toast.error("CJ search failed. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  };

  const importProduct = async (p) => {
    setImporting(p.pid);
    try {
      const v = await api.get(`/admin/cj/variants`, { params: { pid: p.pid } });
      const vid = v.data.variants?.[0]?.vid;
      if (!vid) { toast.error("No variant available to import."); return; }
      const price = parseFloat(prices[p.pid]) || undefined;
      const r = await api.post(`/admin/cj/import`, { pid: p.pid, vid, price, title: p.name });
      toast.success(`Imported! Sells £${r.data.sell_price} (cost £${r.data.supplier_cost}). Orders now auto-fulfil via CJ.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setImporting(null);
    }
  };

  const runBulk = async (items, label) => {
    if (!items.length) { toast("Nothing to import."); return; }
    setBulkLoading(true);
    setBulkFailures([]);
    setBulkProgress({ processed: 0, total: items.length });
    try {
      const r = await api.post(`/admin/cj/bulk-import`, { items });
      const jobId = r.data.job_id;
      toast(`Importing ${r.data.total} items in the background…`);
      const poll = async () => {
        try {
          const jr = await api.get(`/admin/cj/import-job/${jobId}`);
          const j = jr.data;
          setBulkProgress({ processed: j.processed, total: j.total });
          if (j.status === "done") {
            const failures = j.failed || [];
            setBulkFailures(failures);
            if (j.imported_count > 0) {
              toast.success(`Imported ${j.imported_count}${j.failed_count ? `, ${j.failed_count} failed` : ""} (${label})`);
            } else if (j.failed_count) {
              toast.error(`Import failed for all ${j.failed_count} item${j.failed_count === 1 ? "" : "s"} — see the reason below.`);
            }
            setBulkLoading(false);
            setBulkProgress(null);
            return;
          }
        } catch (e) { /* transient — keep polling */ }
        setTimeout(poll, 2000);
      };
      setTimeout(poll, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk import failed");
      setBulkLoading(false);
      setBulkProgress(null);
    }
  };
  const bulkBySku = () => runBulk(bulkText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean), "pasted list");
  const importAllResults = () => runBulk(results.map((p) => ({ pid: p.pid })), "this page");

  return (
    <Card className="bg-white border-black/10 p-5" data-testid="cj-dropshipping-panel">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Truck className="w-5 h-5 text-[#4A5D23]" /> CJ Dropshipping — Auto Fulfilment
        </h3>
        {status && (
          <Badge
            data-testid="cj-status-badge"
            className={status.connected ? "bg-[#4A5D23]/15 text-[#4A5D23]" : "bg-[#B84A28]/15 text-[#B84A28]"}
          >
            {status.connected ? (status.sandbox ? "Connected · TEST mode" : "Connected · LIVE") : "Not connected"}
          </Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        Search CJ's catalog and import products. Imported items are sold on AfriMarket and{" "}
        <strong>fulfilled automatically</strong> — when a customer pays, the order is placed with CJ and tracking flows back to the buyer.
      </p>

      {/* Live connection test — reveals account/API freezes CJ enforces server-side. */}
      <div className="mb-4">
        <Button data-testid="cj-test-connection-btn" onClick={testConnection} disabled={healthLoading} variant="outline" size="sm" className="border-[#4A5D23] text-[#4A5D23]">
          {healthLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Truck className="w-4 h-4 mr-1" />}
          Test CJ connection
        </Button>
        {health && (
          <div
            data-testid="cj-health-result"
            className={`mt-2 rounded-md p-3 text-sm border ${health.ok ? "border-[#4A5D23]/30 bg-[#4A5D23]/8" : "border-[#B84A28]/30 bg-[#B84A28]/8"}`}
          >
            {health.ok ? (
              <span className="text-[#4A5D23] font-semibold">✅ CJ is connected and working — imports should succeed.</span>
            ) : health.frozen ? (
              <span className="text-[#1A2024]">
                <strong className="text-[#B84A28]">CJ account/API is frozen.</strong> CJ is blocking all requests server-side.
                Contact CJ support / your account manager and ask them to <strong>unfreeze API access</strong> (the web login working doesn't mean the API is unfrozen). Then test again here.
              </span>
            ) : (
              <span className="text-[#1A2024]"><strong className="text-[#B84A28]">Not working:</strong> {health.reason}</span>
            )}
          </div>
        )}
      </div>

      <div className="bg-[#F3EFE9] rounded-lg p-3 mb-4">
        <div className="text-sm font-semibold mb-1">Bulk import your selected items</div>
        <p className="text-xs text-muted-foreground mb-2">
          Paste your CJ <strong>SKUs or product links</strong> — one per line (e.g. <code>CJNT242604401AZ</code>). We auto-price at ~1.5× cost.
        </p>
        <textarea
          data-testid="cj-bulk-textarea"
          value={bulkText}
          onChange={(e) => setBulkText(e.target.value)}
          placeholder={"CJNT242604401AZ\nCJNT123456789AB\nhttps://cjdropshipping.com/product/...-p-XXXX.html"}
          rows={4}
          className="w-full text-sm rounded-md border border-black/10 p-2 mb-2 font-mono"
        />
        <Button data-testid="cj-bulk-import-btn" onClick={bulkBySku} disabled={bulkLoading} className="bg-[#4A5D23] text-white">
          {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />}
          <span className="ml-1">
            {bulkLoading && bulkProgress
              ? `Importing ${bulkProgress.processed}/${bulkProgress.total}…`
              : "Import all pasted"}
          </span>
        </Button>

        {bulkFailures.length > 0 && (() => {
          const frozen = bulkFailures.some((f) => /frozen/i.test(f.reason || ""));
          return (
            <div className="mt-3 rounded-md border border-[#B84A28]/30 bg-[#B84A28]/8 p-3" data-testid="cj-bulk-failures">
              <div className="flex items-center gap-2 text-sm font-semibold text-[#B84A28] mb-1">
                <AlertTriangle className="w-4 h-4" /> {bulkFailures.length} item{bulkFailures.length === 1 ? "" : "s"} could not be imported
              </div>
              {frozen ? (
                <p className="text-xs text-[#1A2024]">
                  Your <strong>CJ Dropshipping account is frozen</strong>, so CJ is blocking all product &amp; order requests.
                  Log in to <a href="https://cjdropshipping.com" target="_blank" rel="noreferrer" className="underline text-[#B84A28]">cjdropshipping.com</a>,
                  unfreeze your account (usually a verification or billing step), then try importing again. This is a CJ account status — not an AfriMarket issue.
                </p>
              ) : (
                <ul className="text-xs text-muted-foreground space-y-0.5 max-h-32 overflow-y-auto">
                  {bulkFailures.slice(0, 10).map((f, i) => (
                    <li key={i}><code className="text-[#1A2024]">{f.item}</code> — {f.reason}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })()}
      </div>

      <div className="flex gap-2 mb-4">
        <Input
          data-testid="cj-search-input"
          placeholder="Or search CJ catalog (e.g. ankara dress, earbuds)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
        />
        <Button data-testid="cj-search-btn" onClick={search} disabled={loading} className="bg-[#4A5D23] text-white">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          <span className="ml-1">Search</span>
        </Button>
      </div>

      {results.length > 0 && (
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm text-muted-foreground">{results.length} results</span>
          <Button data-testid="cj-import-all-btn" onClick={importAllResults} disabled={bulkLoading} size="sm" variant="outline" className="border-[#4A5D23] text-[#4A5D23]">
            {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />}
            <span className="ml-1">Import all on this page</span>
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {results.map((p) => (
          <div key={p.pid} className="border border-black/10 rounded-lg p-3 flex flex-col" data-testid={`cj-product-${p.pid}`}>
            {p.image && <img src={p.image} alt={p.name} className="w-full h-32 object-cover rounded mb-2" />}
            <div className="text-sm font-medium line-clamp-2 mb-1">{p.name}</div>
            <div className="text-xs text-muted-foreground mb-2">CJ cost: £{p.price}</div>
            <div className="mt-auto flex gap-2 items-center">
              <Input
                data-testid={`cj-price-${p.pid}`}
                type="number"
                placeholder="Sell £"
                value={prices[p.pid] || ""}
                onChange={(e) => setPrices({ ...prices, [p.pid]: e.target.value })}
                className="h-9 w-24"
              />
              <Button
                data-testid={`cj-import-${p.pid}`}
                size="sm"
                onClick={() => importProduct(p)}
                disabled={importing === p.pid}
                className="bg-[#C05A3C] text-white flex-1"
              >
                {importing === p.pid ? <Loader2 className="w-4 h-4 animate-spin" /> : <PackagePlus className="w-4 h-4" />}
                <span className="ml-1">Import</span>
              </Button>
            </div>
          </div>
        ))}
      </div>
      {results.length > 0 && (
        <p className="text-xs text-muted-foreground mt-3">
          Tip: leave "Sell £" blank to auto-price at ~1.5× cost. You can edit price/details later under Curated Listings.
        </p>
      )}
    </Card>
  );
};

export default CJDropshipping;
